<template>
  <v-card outlined class="pa-4">
    <div class="d-flex align-center mb-2">
      <div>
        <div class="pw-section">{{ title }}</div>
        <div class="caption grey--text text--darken-1">{{ summary }}</div>
      </div>
      <v-spacer />
      <v-btn icon small aria-label="Cerrar listado" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </div>
    <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
    <v-data-table
      v-else
      :headers="headers"
      :items="shownRows"
      :loading="loading"
      :items-per-page="20"
      :no-data-text="isDay ? 'Sin altas ni bajas este día' : 'Sin altas ni bajas este mes'"
      dense
    >
      <template v-slot:item.movement="{ item }">
        <span :class="movementClass(item.movement)">{{ movementLabel(item.movement) }}</span>
        <div v-if="movementDates(item)" class="caption grey--text">{{ movementDates(item) }}</div>
      </template>
      <template v-slot:item.line="{ item }">{{ lineLabel(item.line) }}</template>
      <template v-slot:item.contract="{ item }">
        {{ item.contractCode || 'Sin contrato' }}<span v-if="item.companyName"> · {{ item.companyName }}</span>
      </template>
      <template v-slot:item.isBci="{ item }">{{ item.isBci ? 'Sí' : 'No' }}</template>
      <template v-slot:item.createdAt="{ item }">{{ displayDate(item.createdAt) }}</template>
    </v-data-table>
  </v-card>
</template>

<script>
import { formatNumber } from '@/utils/b2c-report.utils';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const LINE_LABELS = { b2c: 'B2C', b2b2c: 'B2B2C', upsell: 'Upsell' };

// en-GB da 'dd/mm' y '23:26'; es-CL usa guiones.
const SANTIAGO_DAY = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit' });
const SANTIAGO_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'America/Santiago',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

export const MOVEMENT_LABELS = {
  activation: 'Alta',
  churn: 'Baja',
  activationAndChurn: 'Alta y baja'
};

export default {
  name: 'B2CV2BarDetail',
  props: {
    // Un mes: { month: 'YYYY-MM', bciOnly, line }. Un día: { date: 'YYYY-MM-DD', bciOnly, line }.
    // `line` es 'all' o una línea (b2c, b2b2c, upsell): la lista muestra solo esa.
    selection: { type: Object, required: true },
    // [{ movement, line, holderName, email, petName, planName, contractCode, companyName, isBci, createdAt,
    //    activatedAt, churnedAt }] — las dos fechas son la primera alta y la última baja del período.
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' }
  },
  computed: {
    isDay() {
      return Boolean(this.selection.date);
    },
    lineFilter() {
      return this.selection.line && this.selection.line !== 'all' ? this.selection.line : null;
    },
    shownRows() {
      return this.lineFilter ? this.rows.filter((r) => r.line === this.lineFilter) : this.rows;
    },
    headers() {
      return [
        { text: 'Movimiento', value: 'movement', sortable: true },
        { text: 'Línea', value: 'line', sortable: true },
        { text: 'Titular', value: 'holderName', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'Mascota', value: 'petName', sortable: true },
        { text: 'Plan', value: 'planName', sortable: true },
        { text: 'Contrato', value: 'contract', sortable: false },
        { text: 'BCI', value: 'isBci', sortable: true },
        { text: 'Creada', value: 'createdAt', sortable: true }
      ];
    },
    title() {
      const scope = this.lineFilter ? LINE_LABELS[this.lineFilter] : 'B2C/B2B2C';
      const bci = this.selection.bciOnly ? ' (solo BCI)' : '';
      if (this.isDay) return `${scope}${bci} · ${this.displayDate(this.selection.date)}`;
      const [y, m] = this.selection.month.split('-');
      return `${scope}${bci} · ${MONTH_NAMES[Number(m) - 1]} ${y}`;
    },
    /** Lo que entró y salió; una alta y baja en el mismo período cuenta en las dos. */
    summary() {
      if (this.loading) return 'Cargando…';
      const count = (movement) => this.shownRows.filter((r) => r.movement === movement).length;
      const churns = count('churn') + count('activationAndChurn');
      const altas = count('activation') + count('activationAndChurn');
      const period = this.isDay ? 'en el día' : 'en el mes';
      return `${formatNumber(altas)} altas · ${formatNumber(churns)} bajas ${period}`;
    }
  },
  methods: {
    lineLabel(line) {
      return LINE_LABELS[line] || line;
    },
    movementLabel(movement) {
      return MOVEMENT_LABELS[movement] || movement;
    },
    movementClass(movement) {
      if (movement === 'activation') return 'success--text font-weight-medium';
      if (movement === 'churn' || movement === 'activationAndChurn') return 'error--text font-weight-medium';
      return '';
    },
    /**
     * Cuándo ocurrió el movimiento dentro del período, en hora de Chile: la hora si el período
     * es un día, la fecha si es un mes. "Alta y baja" muestra las dos.
     */
    movementDates(row) {
      const format = (iso) => (iso ? (this.isDay ? SANTIAGO_TIME : SANTIAGO_DAY).format(new Date(iso)) : null);
      const activated = format(row.activatedAt);
      const churned = format(row.churnedAt);
      if (activated && churned) return `${activated} → ${churned}`;
      return activated || churned || '';
    },
    displayDate(iso) {
      if (!iso) return '—';
      const [y, m, d] = iso.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
  }
};
</script>
